import React, { useState, useEffect, useMemo } from 'react';
import axios from '../utils/axiosConfig';
import { formatDate } from '../utils/dates';
import { t } from '../utils/translations';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import './Products.css';

const PAGE_SIZE = 20;

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active'
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      alert(t('Failed to fetch categories'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((category) => {
      const name = String(category.name || '').toLowerCase();
      const description = String(category.description || '').toLowerCase();
      const status = String(category.status || '').toLowerCase();
      return name.includes(q) || description.includes(q) || status.includes(q);
    });
  }, [categories, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = filteredCategories.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(safePage * PAGE_SIZE, filteredCategories.length);

  const paginatedCategories = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredCategories.slice(start, start + PAGE_SIZE);
  }, [filteredCategories, safePage]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, page)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`/api/categories/${editingCategory.id}`, formData);
      } else {
        await axios.post('/api/categories', formData);
      }

      setShowModal(false);
      setEditingCategory(null);
      resetForm();
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to save category'));
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      status: category.status || 'active'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Are you sure you want to delete this category?'))) {
      return;
    }

    try {
      await axios.delete(`/api/categories/${id}`);
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to delete category'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      status: 'active'
    });
  };

  const openModal = () => {
    resetForm();
    setEditingCategory(null);
    setShowModal(true);
  };

  if (loading) {
    return <div className="page-container"><div className="container">{t('Loading...')}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>{t('Categories')}</h1>
          <button onClick={openModal} className="btn btn-primary">
            {t('Add Category')}
          </button>
        </div>

        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="search-header">
            <h3>{t('Search Categories')}</h3>
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="btn btn-secondary btn-sm"
            >
              {t('Clear')}
            </button>
          </div>
          <div className="search-filters">
            <div className="search-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>{t('Search')}</label>
                <input
                  type="text"
                  placeholder="Search by name, description, or status"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <div className="search-results-info">
              <span>
                {filteredCategories.length === 0
                  ? `${t('Showing')} 0 ${t('of')} ${categories.length} ${t('categories')}`
                  : `${t('Showing')} ${pageStart}–${pageEnd} ${t('of')} ${filteredCategories.length} ${t('categories')}${
                    filteredCategories.length !== categories.length ? ` (filtered from ${categories.length})` : ''
                  }`}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>{t('Name')}</th>
                <th>{t('Description')}</th>
                <th>{t('Status')}</th>
                <th>{t('Created')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center' }}>
                    {categories.length === 0
                      ? t('No categories found')
                      : t('No categories match your search')}
                  </td>
                </tr>
              ) : (
                paginatedCategories.map(category => (
                  <tr key={category.id}>
                    <td>{category.name}</td>
                    <td>{category.description || '-'}</td>
                    <td>
                      <span className={`status-badge status-${category.status}`}>
                        {category.status}
                      </span>
                    </td>
                    <td>{formatDate(category.created_at)}</td>
                    <td>
                      <KebabMenu
                        menuId={`category-${category.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        label={t('Actions')}
                        items={[
                          { label: t('Edit'), icon: MENU_ICONS.edit, onClick: () => handleEdit(category) },
                          { label: t('Delete'), icon: MENU_ICONS.trash, danger: true, onClick: () => handleDelete(category.id) }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {filteredCategories.length > 0 && (
            <div className="products-pagination">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage <= 1}
              >
                Previous
              </button>
              <div className="products-pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    return Math.abs(page - safePage) <= 1;
                  })
                  .reduce((acc, page, idx, arr) => {
                    if (idx > 0 && page - arr[idx - 1] > 1) acc.push('…');
                    acc.push(page);
                    return acc;
                  }, [])
                  .map((item, idx) => (
                    item === '…' ? (
                      <span key={`ellipsis-${idx}`} className="products-pagination-ellipsis">…</span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        className={`btn btn-sm ${item === safePage ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => goToPage(item)}
                      >
                        {item}
                      </button>
                    )
                  ))}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editingCategory ? t('Edit Category') : t('Add Category')}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('Name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('Description')}</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>{t('Status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">{t('Active')}</option>
                    <option value="inactive">{t('Inactive')}</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingCategory ? t('Update') : t('Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Categories;
